import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { environment } from '../../config/environment';
import { apiRequest } from '../../services/apiClient';
import createSidebarSections from '../../config/sidebarSections';
import DashboardLayout from '../../components/DashboardLayout';
import '../../pages/WelcomePage.css';
import '../../pages/LoginPage.css';

const UserListPage = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('Rakesh');
  const [users, setUsers] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [registerErrors, setRegisterErrors] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '' });
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const renderLogoLoader = () => (
    <div className="mini-logo-loader" role="status" aria-live="polite" aria-label="Loading">
      <span className="loader-orbit loader-orbit-one"></span>
      <span className="loader-orbit loader-orbit-two"></span>
      <span className="loader-orbit loader-orbit-three"></span>
      <img
        src={process.env.PUBLIC_URL + '/images/logo.png'}
        alt="Company Logo"
        className="session-loader-logo"
      />
    </div>
  );

  const userName = useMemo(() => {
    const userStr = localStorage.getItem('authUser') || sessionStorage.getItem('authUser');
    if (!userStr) return '';

    try {
      const user = JSON.parse(userStr);
      return user?.name || user?.fullName || user?.email || '';
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    const applyName = async () => {
      try {
        const validateResponse = await apiRequest(
          environment.endpoints.validate,
          {
            method: 'POST',
          },
          { skipValidation: true }
        );

        const validateData = validateResponse?.data;
        const apiUserName = validateData?.data?.username;
        const normalizedName = typeof apiUserName === 'string' ? apiUserName.trim() : '';

        setDisplayName(normalizedName || userName || 'Rakesh');
      } catch {
        setDisplayName(userName || 'Rakesh');
      }
    };

    applyName();
  }, [userName]);

  const fetchUsers = async () => {
    setIsUsersLoading(true);
    setUsersError('');

    try {
      const { ok, data } = await apiRequest(environment.endpoints.users, {
        method: 'POST',
      });

      if (!ok || data?.response !== true) {
        throw new Error(data?.message || 'Unable to fetch users.');
      }

      const userList = Array.isArray(data?.data) ? data.data : [];
      setUsers(userList);
      setVisiblePasswords({});
    } catch (error) {
      setUsers([]);
      setUsersError(error?.message || 'Unable to fetch users.');
    } finally {
      setIsUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const validateRegisterForm = () => {
    const nextErrors = {};

    if (!registerForm.name.trim()) {
      nextErrors.name = 'Name is required.';
    }

    if (!registerForm.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(registerForm.email.trim())) {
      nextErrors.email = 'Enter a valid email.';
    }

    if (!registerForm.password.trim()) {
      nextErrors.password = 'Password is required.';
    } else if (registerForm.password.trim().length < 6) {
      nextErrors.password = 'Password must be at least 6 characters.';
    }

    if (!registerForm.confirmPassword.trim()) {
      nextErrors.confirmPassword = 'Confirm password is required.';
    } else if (registerForm.password !== registerForm.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match.';
    }

    setRegisterErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRegisterChange = (event) => {
    const { name, value } = event.target;
    setRegisterForm((prev) => ({ ...prev, [name]: value }));

    if (registerErrors[name]) {
      setRegisterErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();

    if (!validateRegisterForm()) {
      return;
    }

    setIsRegistering(true);

    try {
      const { ok, data } = await apiRequest(environment.endpoints.register, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerForm.name.trim(),
          email: registerForm.email.trim(),
          password: registerForm.password,
        }),
      });

      // Keep register flow same as login: loader first, then alert.
      setIsRegistering(false);

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || 'Unable to register user.',
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      const successAlert = await Swal.fire({
        icon: 'success',
        text: data?.message || 'User registered successfully.',
        confirmButtonColor: '#0f7a8f',
      });

      if (successAlert.isConfirmed) {
        setRegisterForm({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
        });
        setRegisterErrors({});
        setIsRegisterModalOpen(false);
        await fetchUsers();
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        text: error?.message || 'Unable to register user.',
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const handleLogout = async () => {
    try {
      await apiRequest(
        environment.endpoints.logout,
        {
          method: 'POST',
        },
        { skipValidation: true }
      );
    } catch {
      // Ignore logout API failure and continue client-side cleanup.
    } finally {
      localStorage.removeItem('authUser');
      sessionStorage.removeItem('authUser');
      localStorage.setItem('logoutRequested', 'true');
      sessionStorage.setItem('logoutRequested', 'true');
      navigate('/login', { replace: true });
    }
  };

  const togglePasswordVisibility = (rowKey) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const openViewModal = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      password: user?.password || '',
    });
    setEditErrors({});
    setIsEditModalOpen(true);
  };

  const validateEditForm = () => {
    const nextErrors = {};

    if (!editForm.name.trim()) {
      nextErrors.name = 'Name is required.';
    }

    if (!editForm.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^(?!.*\.{2})[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(editForm.email.trim())) {
      nextErrors.email = 'Enter a valid email.';
    }

    if (!editForm.password.trim()) {
      nextErrors.password = 'Password is required.';
    } else if (editForm.password.trim().length < 6) {
      nextErrors.password = 'Password must be at least 6 characters.';
    }

    setEditErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));

    if (editErrors[name]) {
      setEditErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser?.id) {
      await Swal.fire({
        icon: 'error',
        text: 'User ID not found for update.',
        confirmButtonColor: '#c7374e',
      });
      return;
    }

    if (!validateEditForm()) {
      return;
    }

    setIsUpdating(true);

    try {
      const { ok, data } = await apiRequest(
        `${environment.endpoints.updateUser}/${selectedUser.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: editForm.name.trim(),
            email: editForm.email.trim(),
            password: editForm.password,
          }),
        }
      );

      setIsUpdating(false);

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || 'Unable to update user.',
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      const successAlert = await Swal.fire({
        icon: 'success',
        text: data?.message || 'User updated successfully.',
        confirmButtonColor: '#0f7a8f',
      });

      if (successAlert.isConfirmed) {
        setIsEditModalOpen(false);
        setSelectedUser(null);
        await fetchUsers();
      }
    } catch (error) {
      setIsUpdating(false);
      await Swal.fire({
        icon: 'error',
        text: error?.message || 'Unable to update user.',
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const sections = useMemo(
    () => createSidebarSections(navigate, 'user-list'),
    [navigate]
  );

  return (
    <DashboardLayout
      displayName={displayName}
      pageTitle="User List"
      dashboard={{
        active: false,
        onClick: () => navigate('/welcome'),
      }}
      sections={sections}
      onLogout={handleLogout}
    >
      <section className="dashboard-card">
            <div className="users-card-header">
              <h3>User Management - List</h3>
              <button
                type="button"
                className="register-user-btn"
                onClick={() => {
                  setRegisterErrors({});
                  setIsRegisterModalOpen(true);
                }}
              >
                + Add User
              </button>
            </div>

            {isUsersLoading && (
              <div className="users-status" role="status" aria-live="polite">
                <span className="users-loader" aria-hidden="true"></span>
              </div>
            )}

            {!isUsersLoading && usersError && (
              <p className="users-status users-error">{usersError}</p>
            )}

            {!isUsersLoading && !usersError && (
              <div className="users-table-wrap">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user, index) => {
                        const rowKey = user.id || user.email || `row-${index}`;
                        const isPasswordVisible = Boolean(visiblePasswords[rowKey]);

                        return (
                        <tr key={rowKey}>
                          <td>{index + 1}</td>
                          <td>{user.name || '-'}</td>
                          <td>{user.email || '-'}</td>
                          <td>
                            {user.password ? (
                              <div className="user-password-cell">
                                <span>{isPasswordVisible ? user.password : '******'}</span>
                                <button
                                  type="button"
                                  className="user-password-toggle"
                                  onClick={() => togglePasswordVisibility(rowKey)}
                                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                                >
                                  {isPasswordVisible ? '🙈' : '👁'}
                                </button>
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <div className="user-action-cell">
                              <button
                                type="button"
                                className="action-icon-btn"
                                onClick={() => openViewModal(user)}
                                aria-label="View user details"
                                title="View"
                              >
                                👁
                              </button>
                              <button
                                type="button"
                                className="action-icon-btn edit-action-btn"
                                onClick={() => openEditModal(user)}
                                aria-label="Edit user"
                                title="Edit"
                              >
                                <span className="action-edit-icon">✏</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="users-empty">No users found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
      </section>

      {isRegisterModalOpen && (
        <div
          className="register-modal-overlay"
          onClick={() => {
            if (!isRegistering) {
              setIsRegisterModalOpen(false);
            }
          }}
          role="presentation"
        >
          <section
            className="register-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="register-user-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="register-modal-header">
              <h3 id="register-user-title">Add User</h3>
              <button
                type="button"
                className="register-modal-close"
                aria-label="Close add user form"
                onClick={() => {
                  if (!isRegistering) {
                    setIsRegisterModalOpen(false);
                  }
                }}
              >
                X
              </button>
            </div>

            <form className="register-user-form" onSubmit={handleRegisterSubmit} noValidate>
              <div className="register-user-grid">
                <div className="register-field">
                  <label htmlFor="register-name">Name</label>
                  <input
                    id="register-name"
                    name="name"
                    type="text"
                    value={registerForm.name}
                    onChange={handleRegisterChange}
                    placeholder="Enter name"
                    disabled={isRegistering}
                  />
                  {registerErrors.name && <p className="register-error">{registerErrors.name}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="register-email">Email</label>
                  <input
                    id="register-email"
                    name="email"
                    type="email"
                    value={registerForm.email}
                    onChange={handleRegisterChange}
                    placeholder="you@example.com"
                    disabled={isRegistering}
                  />
                  {registerErrors.email && <p className="register-error">{registerErrors.email}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="register-password">Password</label>
                  <input
                    id="register-password"
                    name="password"
                    type="password"
                    value={registerForm.password}
                    onChange={handleRegisterChange}
                    placeholder="Enter password"
                    disabled={isRegistering}
                  />
                  {registerErrors.password && <p className="register-error">{registerErrors.password}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="register-confirm-password">Confirm Password</label>
                  <input
                    id="register-confirm-password"
                    name="confirmPassword"
                    type="password"
                    value={registerForm.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Confirm password"
                    disabled={isRegistering}
                  />
                  {registerErrors.confirmPassword && (
                    <p className="register-error">{registerErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              <button type="submit" className="register-user-btn" disabled={isRegistering}>
                Add User
              </button>
            </form>
          </section>
        </div>
      )}

      {isViewModalOpen && selectedUser && (
        <div
          className="register-modal-overlay"
          onClick={() => setIsViewModalOpen(false)}
          role="presentation"
        >
          <section
            className="register-modal animated-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-user-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="register-modal-header">
              <h3 id="view-user-title">View User</h3>
              <button
                type="button"
                className="register-modal-close"
                aria-label="Close view user modal"
                onClick={() => setIsViewModalOpen(false)}
              >
                X
              </button>
            </div>

            <div className="view-user-details">
              <div className="view-row"><span>Name</span><strong>{selectedUser.name || '-'}</strong></div>
              <div className="view-row"><span>Email</span><strong>{selectedUser.email || '-'}</strong></div>
              <div className="view-row"><span>Password</span><strong>{selectedUser.password || '-'}</strong></div>
            </div>
          </section>
        </div>
      )}

      {isEditModalOpen && selectedUser && (
        <div
          className="register-modal-overlay"
          onClick={() => {
            if (!isUpdating) {
              setIsEditModalOpen(false);
            }
          }}
          role="presentation"
        >
          <section
            className="register-modal animated-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-user-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="register-modal-header">
              <h3 id="edit-user-title">Edit User</h3>
              <button
                type="button"
                className="register-modal-close"
                aria-label="Close edit user modal"
                onClick={() => {
                  if (!isUpdating) {
                    setIsEditModalOpen(false);
                  }
                }}
              >
                X
              </button>
            </div>

            <form className="register-user-form" onSubmit={handleEditSubmit} noValidate>
              <div className="register-user-grid">
                <div className="register-field">
                  <label htmlFor="edit-name">Name</label>
                  <input
                    id="edit-name"
                    name="name"
                    type="text"
                    value={editForm.name}
                    onChange={handleEditChange}
                    placeholder="Enter name"
                    disabled={isUpdating}
                  />
                  {editErrors.name && <p className="register-error">{editErrors.name}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="edit-email">Email</label>
                  <input
                    id="edit-email"
                    name="email"
                    type="email"
                    value={editForm.email}
                    onChange={handleEditChange}
                    placeholder="you@example.com"
                    disabled={isUpdating}
                  />
                  {editErrors.email && <p className="register-error">{editErrors.email}</p>}
                </div>

                <div className="register-field">
                  <label htmlFor="edit-password">Password</label>
                  <input
                    id="edit-password"
                    name="password"
                    type="password"
                    value={editForm.password}
                    onChange={handleEditChange}
                    placeholder="Enter password"
                    disabled={isUpdating}
                  />
                  {editErrors.password && <p className="register-error">{editErrors.password}</p>}
                </div>
              </div>

              <button type="submit" className="register-user-btn" disabled={isUpdating}>
                Update User
              </button>
            </form>
          </section>
        </div>
      )}

      {(isRegistering || isUpdating) && (
        <div className="api-loader-overlay">
          {renderLogoLoader()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default UserListPage;
